import type { AuthMeResponse, AuthResponse, RefreshTokenResponse } from '@/types/api'
import { clearTokens, getAccessToken, getRefreshToken, setAccessToken, setRefreshToken } from '@/auth/tokenStore'

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  auth?: boolean
  skipRefresh?: boolean
}

let refreshPromise: Promise<boolean> | null = null
let onAuthFailure: (() => void) | null = null

export function setAuthFailureHandler(handler: (() => void) | null): void {
  onAuthFailure = handler
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    const res = await fetch('/auth/refreshToken', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) {
      clearTokens()
      return false
    }
    const data = (await res.json()) as RefreshTokenResponse
    setAccessToken(data.token)
    setRefreshToken(data.refreshToken)
    return true
  } catch {
    clearTokens()
    return false
  }
}

function ensureRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

function messageFromBody(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback
  const o = body as Record<string, unknown>
  if (typeof o.message === 'string' && o.message) return o.message
  if (Array.isArray(o.messages) && o.messages.length) return String(o.messages[0])
  if (typeof o.title === 'string') return o.title
  if (typeof body === 'string') return body
  return fallback
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = true, skipRefresh = false, headers, ...rest } = options
  const h = new Headers(headers)
  if (body !== undefined && !h.has('Content-Type')) {
    h.set('Content-Type', 'application/json')
  }
  if (auth) {
    const token = getAccessToken()
    if (token) h.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(path, {
    ...rest,
    headers: h,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (res.status === 401 && auth && !skipRefresh) {
    const ok = await ensureRefresh()
    if (ok) {
      return apiRequest<T>(path, { ...options, skipRefresh: true })
    }
    onAuthFailure?.()
    throw new ApiError(401, 'Session expired. Please sign in again.')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = text
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, messageFromBody(parsed, res.statusText || 'Request failed'), parsed)
  }

  return parsed as T
}

/** Authenticated binary download (triggers browser save dialog). */
export async function apiDownload(path: string, fallbackFilename: string): Promise<void> {
  const token = getAccessToken()
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res = await fetch(path, { headers })
  if (res.status === 401) {
    const ok = await ensureRefresh()
    if (!ok) {
      onAuthFailure?.()
      throw new ApiError(401, 'Session expired. Please sign in again.')
    }
    const retryHeaders = new Headers()
    const next = getAccessToken()
    if (next) retryHeaders.set('Authorization', `Bearer ${next}`)
    res = await fetch(path, { headers: retryHeaders })
  }

  if (!res.ok) {
    let parsed: unknown = null
    try {
      parsed = await res.json()
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, messageFromBody(parsed, res.statusText || 'Download failed'), parsed)
  }

  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i.exec(disposition)
  const rawName = match?.[1] ? decodeURIComponent(match[1].replace(/"/g, '').trim()) : fallbackFilename
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = rawName || fallbackFilename
  a.click()
  URL.revokeObjectURL(url)
}

export const authApi = {
  login(username: string, password: string) {
    return apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      auth: false,
      body: { username, password },
    })
  },
  logout(refreshToken: string) {
    return apiRequest<string>('/auth/logout', {
      method: 'POST',
      auth: false,
      body: { refreshToken },
    })
  },
  refresh(refreshToken: string) {
    return apiRequest<RefreshTokenResponse>('/auth/refreshToken', {
      method: 'POST',
      auth: false,
      skipRefresh: true,
      body: { refreshToken },
    })
  },
  me() {
    return apiRequest<AuthMeResponse>('/auth/me', { auth: true })
  },
}
